import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  LayoutDashboard, Users, Home, UserSquare2, Receipt, Wallet, CalendarClock,
  FileBarChart, History, Settings, LogOut, Building2, Menu, ShieldAlert, CreditCard, Mail
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import NotificationBell from "@/components/NotificationBell";
import ImpersonationRibbon from "@/components/ImpersonationRibbon";
import ReadOnlyBanner from "@/components/ReadOnlyBanner";

const NAV = [
  { to: "/", label: "Tableau de bord", icon: LayoutDashboard, end: true, testid: "nav-dashboard" },
  { to: "/proprietaires", label: "Propriétaire", icon: Users, testid: "nav-proprietaires" },
  { to: "/maisons", label: "Maison", icon: Home, testid: "nav-maisons" },
  { to: "/locataires", label: "Locataire", icon: UserSquare2, testid: "nav-locataires" },
  { to: "/depenses", label: "Dépense", icon: Receipt, testid: "nav-depenses" },
  { to: "/paiements", label: "Paiement", icon: Wallet, testid: "nav-paiements" },
  { to: "/sejours", label: "Séjour", icon: CalendarClock, testid: "nav-sejours" },
  { to: "/rapports", label: "Rapports", icon: FileBarChart, testid: "nav-rapports" },
  { to: "/historique", label: "Historique", icon: History, testid: "nav-historique" },
  { to: "/configuration", label: "Configuration", icon: Settings, testid: "nav-configuration" },
];

const SIDEBAR_BG = "#1F5F5B";

function SidebarContent({ user, doLogout, onNavigate }) {
  return (
    <div className="h-full flex flex-col" style={{ backgroundColor: SIDEBAR_BG }} data-testid="sidebar-content">
      <div className="px-6 py-6 border-b border-white/10">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-[#1F5F5B] border border-white/10 flex items-center justify-center shadow-lg shadow-black/30 overflow-hidden">
            <img src="/logo.png" alt="rimmoGET" className="w-11 h-11 object-cover" />
          </div>
          <div>
            <div className="font-heading font-bold text-white text-lg leading-none">rimmoGET</div>
            <div className="text-[11px] uppercase tracking-[0.18em] text-amber-300/90 mt-1">Gestion locative</div>
          </div>
        </div>
      </div>

      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {NAV
          .filter((item) => {
            if (user?.role === "dev" && !user?.impersonating) {
              return item.to === "/";
            }
            return true;
          })
          .map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={item.testid}
              onClick={onNavigate}
              className={({ isActive }) =>
                `group flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                  isActive
                    ? "bg-orange-500 text-white shadow-md shadow-orange-900/30"
                    : "text-teal-100/70 hover:text-white hover:bg-white/10"
                }`
              }
            >
              <item.icon className="w-[18px] h-[18px]" strokeWidth={1.75} />
              <span>{item.label}</span>
            </NavLink>
          ))}
        {user?.role === "dev" && (
          <>
            <NavLink
              to="/dev"
              end
              data-testid="nav-dev"
              onClick={onNavigate}
              className={({ isActive }) =>
                `group flex items-center gap-3 px-3 py-2.5 mt-4 rounded-lg text-sm font-medium transition-all duration-200 border ${
                  isActive
                    ? "bg-amber-400 text-amber-950 border-amber-300 shadow-md"
                    : "text-amber-200 border-amber-300/30 hover:text-white hover:bg-amber-400/10"
                }`
              }
            >
              <ShieldAlert className="w-[18px] h-[18px]" strokeWidth={1.75} />
              <span>Espace DEV</span>
            </NavLink>
            <NavLink
              to="/dev/users"
              data-testid="nav-dev-users"
              onClick={onNavigate}
              className={({ isActive }) =>
                `group flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 border ${
                  isActive
                    ? "bg-amber-400 text-amber-950 border-amber-300 shadow-md"
                    : "text-amber-200 border-amber-300/30 hover:text-white hover:bg-amber-400/10"
                }`
              }
            >
              <Users className="w-[18px] h-[18px]" strokeWidth={1.75} />
              <span>Utilisateurs (DEV)</span>
            </NavLink>
            <NavLink
              to="/dev/paiements"
              data-testid="nav-dev-paiements"
              onClick={onNavigate}
              className={({ isActive }) =>
                `group flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 border ${
                  isActive
                    ? "bg-amber-400 text-amber-950 border-amber-300 shadow-md"
                    : "text-amber-200 border-amber-300/30 hover:text-white hover:bg-amber-400/10"
                }`
              }
            >
              <CreditCard className="w-[18px] h-[18px]" strokeWidth={1.75} />
              <span>Paiements (DEV)</span>
            </NavLink>
            <NavLink
              to="/dev/emails"
              data-testid="nav-dev-emails"
              onClick={onNavigate}
              className={({ isActive }) =>
                `group flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 border ${
                  isActive
                    ? "bg-amber-400 text-amber-950 border-amber-300 shadow-md"
                    : "text-amber-200 border-amber-300/30 hover:text-white hover:bg-amber-400/10"
                }`
              }
            >
              <Mail className="w-[18px] h-[18px]" strokeWidth={1.75} />
              <span>Notifications &amp; Email</span>
            </NavLink>
          </>
        )}
      </nav>

      <div className="px-4 py-4 border-t border-white/10">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-9 h-9 rounded-full bg-amber-400/20 border border-amber-300/40 flex items-center justify-center text-amber-200 font-heading font-semibold text-sm">
            {(user?.name || user?.email || "?").slice(0, 1).toUpperCase()}
          </div>
          <div className="min-w-0">
            <div className="text-sm text-white font-medium truncate">{user?.name || "Utilisateur"}</div>
            <div className="text-[11px] text-teal-200/70 truncate">{user?.email}</div>
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={doLogout}
          data-testid="btn-logout"
          className="w-full justify-start text-teal-100/70 hover:text-white hover:bg-white/10"
        >
          <LogOut className="w-4 h-4 mr-2" /> Se déconnecter
        </Button>
        <div className="mt-3 pt-3 border-t border-white/10 text-center">
          <div className="text-[10px] tracking-wider text-teal-200/50" data-testid="app-copyright">
            © 2026 copyright <span className="text-amber-300/80 font-medium">rpstogo</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AppShell() {
  const { user, logout, refreshMe } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const doLogout = async () => { await logout(); navigate("/login"); };

  // Close mobile drawer on route change
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  useEffect(() => {
    const onImpersonate = () => { refreshMe(); };
    window.addEventListener("rimmoget:impersonation", onImpersonate);
    return () => window.removeEventListener("rimmoget:impersonation", onImpersonate);
  }, [refreshMe]);

  return (
    <div className="min-h-screen flex" style={{ backgroundColor: "#F8F2E7" }}>
      {/* Desktop sidebar */}
      <aside className="w-64 shrink-0 hidden md:flex flex-col text-teal-50" data-testid="app-sidebar">
        <SidebarContent user={user} doLogout={doLogout} />
      </aside>

      {/* Mobile drawer */}
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="p-0 w-72 max-w-[85vw] border-0" data-testid="mobile-sidebar">
          <SidebarContent user={user} doLogout={doLogout} onNavigate={() => setMobileOpen(false)} />
        </SheetContent>
      </Sheet>

      {/* Main content */}
      <main className="flex-1 min-w-0 overflow-x-hidden flex flex-col">
        <ImpersonationRibbon onChange={refreshMe} />
        <ReadOnlyBanner user={user} />
        {/* Top bar */}
        <div className="sticky top-0 z-30" style={{ backgroundColor: SIDEBAR_BG }}>
          <div className="flex items-center justify-between px-4 sm:px-6 md:px-8 py-3 text-white gap-3">
            <div className="flex items-center gap-2 sm:gap-3 min-w-0">
              {/* Mobile menu trigger */}
              <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                <SheetTrigger asChild>
                  <button
                    className="md:hidden p-2 -ml-2 rounded-lg hover:bg-white/10 transition-colors"
                    data-testid="btn-mobile-menu"
                    aria-label="Ouvrir le menu"
                  >
                    <Menu className="w-5 h-5 text-teal-100" />
                  </button>
                </SheetTrigger>
              </Sheet>
              <Menu className="w-5 h-5 text-teal-100/80 hidden md:block" />
              {user?.society?.nom && (
                <div className="flex items-center gap-1.5 sm:gap-2 px-2 sm:px-3 py-1.5 rounded-md bg-white/10 border border-white/15 min-w-0 max-w-[55vw] sm:max-w-none">
                  <Building2 className="w-3.5 h-3.5 text-amber-300 shrink-0" />
                  <span className="text-xs uppercase tracking-wider text-teal-100/80 hidden md:inline">Société</span>
                  <span className="text-xs sm:text-sm font-medium text-white truncate">{user.society.nom}</span>
                </div>
              )}
              {user?.role === "dev" && (
                <span className="hidden md:inline text-[10px] uppercase tracking-[0.18em] font-bold text-amber-300 px-2 py-0.5 rounded bg-amber-300/10 border border-amber-300/30">
                  DEV
                </span>
              )}
            </div>
            <div className="flex items-center gap-2 sm:gap-4 shrink-0">
              <NotificationBell />
              <div className="flex items-center gap-2 pl-2 sm:pl-3 border-l border-white/15">
                <div className="w-8 h-8 rounded-full bg-amber-400/25 border border-amber-300/40 flex items-center justify-center text-amber-200 text-xs font-semibold">
                  {(user?.name || user?.email || "?").slice(0, 1).toUpperCase()}
                </div>
                <span className="hidden lg:block text-sm font-medium text-white truncate max-w-[140px]">{user?.name || "Utilisateur"}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 md:px-8 py-4 sm:py-6 md:py-8 animate-fade-in w-full">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
